import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  cwGetWorkerById,
  cwUpdateWorker,
  cwListProjects,
  cwSwitchWorkerProject,
} from "../../../services/contractWorkers";

// ─────────────────────────────────────────────────────────────────────────────
// Contract Workers → Edit Worker (super-admin only)
// Edits name, contract company, work profile, shift, project and the photo.
// Changing the project routes through the dedicated move endpoint so the
// worker's projectHistory is recorded.
// ─────────────────────────────────────────────────────────────────────────────

const SHIFT_OPTIONS = ["Morning", "Night"];
const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:5001";
const resolveUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${API_ORIGIN}${u}`;

const EditContractWorker = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const role = useSelector(selectUserRole);

  const [form, setForm] = useState({
    name: "",
    contractCompany: "",
    workProfile: "",
    shift: "Morning",
  });
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [originalProjectId, setOriginalProjectId] = useState("");
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    cwListProjects()
      .then((res) => setProjects(res.data?.projects || []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    cwGetWorkerById(id)
      .then((res) => {
        const w = res.data?.worker;
        if (!w) {
          setNotFound(true);
          return;
        }
        setForm({
          name: w.name || "",
          contractCompany: w.contractCompany || "",
          workProfile: w.workProfile || "",
          shift: SHIFT_OPTIONS.includes(w.shift) ? w.shift : "Morning",
        });
        const pid = w.projectId?._id?.toString() || "";
        setProjectId(pid);
        setOriginalProjectId(pid);
        setCurrentPhoto(resolveUrl(w.photoUrl));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (role !== "super-admin") return <Navigate to="/" replace />;

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Worker name is required.";
    if (!form.workProfile.trim())
      errs.workProfile = "Work profile is required.";
    if (!projectId) errs.projectId = "Select the project.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("contractCompany", form.contractCompany.trim());
      fd.append("workProfile", form.workProfile.trim());
      fd.append("shift", form.shift);
      if (photo) fd.append("photo", photo);

      await cwUpdateWorker(id, fd);
      // Project moves go through the dedicated endpoint (records projectHistory).
      if (projectId && projectId !== originalProjectId) {
        await cwSwitchWorkerProject(id, projectId);
      }
      Swal.fire({
        icon: "success",
        title: "Worker updated",
        timer: 1400,
        showConfirmButton: false,
      });
      navigate("/contract-workers/roster");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to update worker.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return <Navigate to="/contract-workers/roster" replace />;

  return (
    <>
      <PageTitle activeMenu="Edit Worker" motherMenu="Contract Workers" />
      <div className="row">
        <div className="col-xl-8 col-lg-10">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Worker Details</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">Loading…</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Project (Appointed At)*
                        </label>
                        <select
                          className="form-control"
                          value={projectId}
                          onChange={(e) => setProjectId(e.target.value)}
                        >
                          <option value="">Select project…</option>
                          {projects.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.projectName}
                              {p.city ? ` — ${p.city}` : ""}
                            </option>
                          ))}
                        </select>
                        {errors.projectId ? (
                          <small className="text-danger">
                            {errors.projectId}
                          </small>
                        ) : (
                          projectId !== originalProjectId && (
                            <small className="text-warning">
                              Saving will move this worker to the selected
                              project.
                            </small>
                          )
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Worker Name*</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.name}
                          onChange={set("name")}
                        />
                        {errors.name && (
                          <small className="text-danger">{errors.name}</small>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Contract With Company{" "}
                          <span className="text-muted">(optional)</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Contractor / agency name"
                          value={form.contractCompany}
                          onChange={set("contractCompany")}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Work Profile*</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.workProfile}
                          onChange={set("workProfile")}
                        />
                        {errors.workProfile && (
                          <small className="text-danger">
                            {errors.workProfile}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Shift*</label>
                        <select
                          className="form-control"
                          value={form.shift}
                          onChange={set("shift")}
                        >
                          {SHIFT_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Photo <span className="text-muted">(optional)</span>
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={onPhoto}
                        />
                        {(photoPreview || currentPhoto) && (
                          <img
                            src={photoPreview || currentPhoto}
                            alt="worker"
                            style={{
                              marginTop: 8,
                              width: 84,
                              height: 84,
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-primary me-2"
                        disabled={submitting}
                      >
                        {submitting ? "Saving…" : "Save Changes"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger light"
                        onClick={() => navigate("/contract-workers/roster")}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditContractWorker;
